import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { STUDIO_COPY } from '../config';

type AdjustedReelPlayerProps = {
  /** Watermarked reel signed URL (null while rebuilding or unavailable). */
  reelUrl: string | null | undefined;
  /** True while refine re-render is in flight. */
  rebuilding?: boolean;
  error?: string | null;
  /** True when timeline edits happened after the last rebuild. */
  stale?: boolean;
  testId?: string;
  /** Cache-bust remount after mix/watermark updates (job.updated_at). */
  remountKey?: string;
  /** V2 stage surface — live-preview badge; default keeps existing copy. */
  variant?: 'default' | 'live';
  onPlaybackTime?: (sec: number, duration: number) => void;
};

export type AdjustedReelPlayerHandle = {
  seekTo: (sec: number) => void;
  /** Stage <video> for V2 record-to-picture. */
  getVideo: () => HTMLVideoElement | null;
};

/**
 * Watermarked server rebuild player. Instant arrangement preview lives on
 * ClipSegmentPlayer playlist mode — this surface is the exact render (music).
 */
const AdjustedReelPlayer = forwardRef<
  AdjustedReelPlayerHandle,
  AdjustedReelPlayerProps
>(function AdjustedReelPlayer(
  {
    reelUrl,
    rebuilding = false,
    error = null,
    stale = false,
    testId = 'studio-adjusted-reel-player',
    remountKey,
    variant = 'default',
    onPlaybackTime,
  },
  ref
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const onPlaybackTimeRef = useRef(onPlaybackTime);
  onPlaybackTimeRef.current = onPlaybackTime;

  useImperativeHandle(ref, () => ({
    getVideo: () => videoRef.current,
    seekTo: (sec) => {
      const video = videoRef.current;
      if (!video) return;
      const duration = Number.isFinite(video.duration) ? video.duration : sec;
      const next = Math.max(0, Math.min(duration, sec));
      try {
        video.currentTime = next;
      } catch {
        // ignore
      }
      onPlaybackTimeRef.current?.(next, duration);
    },
  }));

  useEffect(() => {
    setLoadError(null);
    const video = videoRef.current;
    if (!video || !reelUrl || rebuilding) return;
    if (video.src !== reelUrl) {
      video.src = reelUrl;
    }
    // Click-to-play: load from top; user presses play (avoids autoplay block).
    try {
      video.currentTime = 0;
    } catch {
      // ignore
    }
  }, [reelUrl, rebuilding]);

  return (
    <div
      className="studio-customize-player-card rounded-lg overflow-hidden border border-white/10 bg-black space-y-2"
      data-testid={testId}
      data-stale={stale ? 'true' : 'false'}
    >
      <div className="px-3 pt-3">
        <p className="text-sm font-medium">
          {variant === 'live' ? STUDIO_COPY.recapEditorV2LivePreview : 'Watermarked preview'}
        </p>
        <p className="text-xs text-[var(--studio-muted)]">
          {variant === 'live'
            ? STUDIO_COPY.customizeInstantSubtitle
            : 'Exact server render (with music) — Accept unlocks the clean download.'}
        </p>
        {stale && !rebuilding ? (
          <p
            className="text-xs text-[var(--studio-accent-bright)] mt-1"
            data-testid={`${testId}-stale`}
          >
            {STUDIO_COPY.customizeReelStale}
          </p>
        ) : null}
      </div>

      {rebuilding ? (
        <div
          className="studio-customize-player-media studio-customize-player-placeholder flex flex-col items-center justify-center gap-2 text-[var(--studio-muted)]"
          data-testid={`${testId}-rendering`}
        >
          <p className="text-sm">Rendering watermarked preview…</p>
          <p className="text-xs">
            Arrangement preview stays available — this builds the exact file.
          </p>
        </div>
      ) : reelUrl ? (
        <>
          <div className="studio-customize-player-media">
            <video
              key={remountKey || reelUrl}
              ref={videoRef}
              className="studio-customize-player-video"
              controls
              playsInline
              preload="metadata"
              src={reelUrl}
              data-testid={`${testId}-video`}
              onTimeUpdate={(event) => {
                const video = event.currentTarget;
                const duration = Number.isFinite(video.duration) ? video.duration : 0;
                onPlaybackTimeRef.current?.(video.currentTime, duration);
              }}
              onError={() => {
                setLoadError("Couldn't load preview — refresh and try again.");
              }}
            />
          </div>
          {loadError ? (
            <p
              className="text-xs text-center text-[var(--studio-danger)] px-3 pb-2"
              role="alert"
              data-testid={`${testId}-error`}
            >
              {loadError}
            </p>
          ) : null}
        </>
      ) : (
        <div
          className="studio-customize-player-media studio-customize-player-placeholder flex items-center justify-center text-[var(--studio-muted)] px-4 text-center"
          data-testid={`${testId}-empty`}
        >
          <p className="text-sm">
            {error ||
              'Render watermarked preview to hear music and see the exact cut here.'}
          </p>
        </div>
      )}
    </div>
  );
});

export default AdjustedReelPlayer;
